
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { analyzeWebsiteWithAI } from "@/utils/websiteAnalyzer";

interface UseWebsiteAnalyzerProps {
  businessData: {
    name: string;
    industry: string;
    website: string;
    description: string;
    brandVoice: string;
    keywords: string[];
    colors: string[];
    socialMedia?: Record<string, string>;
    lastAnalyzedAt?: number;
  };
  updateBusinessData: (key: string, value: string | string[] | Record<string, string> | number) => void;
}

export const useWebsiteAnalyzer = ({ businessData, updateBusinessData }: UseWebsiteAnalyzerProps) => {
  const { toast } = useToast();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);

  const analyzeWebsite = async () => {
    // Check if analysis was done in the last 24 hours (86400000 ms)
    const now = Date.now();
    const timeSinceLastAnalysis = now - (businessData.lastAnalyzedAt || 0);
    const analysisCooldown = 86400000; // 24 hours in milliseconds

    if (businessData.lastAnalyzedAt && timeSinceLastAnalysis < analysisCooldown) {
      const hoursRemaining = Math.ceil((analysisCooldown - timeSinceLastAnalysis) / 3600000);
      toast({
        title: "Analysis limit reached",
        description: `You can analyze your website again in ${hoursRemaining} hour${hoursRemaining > 1 ? 's' : ''}.`,
        variant: "default",
      });
      return;
    }

    if (!businessData.website) {
      toast({
        title: "Website URL required",
        description: "Please enter your business website URL to analyze.",
        variant: "destructive",
      });
      return;
    }

    setIsAnalyzing(true);
    setAnalysisError(null);
    setAttempts(prev => prev + 1);
    
    try {
      // Add https:// if not present
      let url = businessData.website;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
        updateBusinessData('website', url);
      }
      
      console.log(`Analyzing website: ${url} (attempt ${attempts + 1})`);
      const data = await analyzeWebsiteWithAI(url);
      
      // Update business data with analysis results
      if (data.keywords && data.keywords.length > 0) {
        updateBusinessData('keywords', data.keywords);
        // Also store as business_tags for persistence
        updateBusinessData('business_tags', data.keywords);
      }
      
      if (data.colors && data.colors.length > 0) {
        updateBusinessData('colors', data.colors);
      }
      
      // Suggest description if empty
      if (!businessData.description && data.description) {
        updateBusinessData('description', data.description);
      }
      
      // Suggest brand voice if empty
      if (!businessData.brandVoice) {
        let voiceSuggestion = `${data.tone || 'professional'} and ${data.formality || 'formal'}`;
        updateBusinessData('brandVoice', voiceSuggestion);
      }
      
      // Update social media profiles if found
      if (data.socialMedia && Object.keys(data.socialMedia).length > 0) {
        const existingSocialMedia = businessData.socialMedia || {};
        updateBusinessData('socialMedia', { ...existingSocialMedia, ...data.socialMedia });
      }
      
      // Store the timestamp of successful analysis
      updateBusinessData('lastAnalyzedAt', now);
      
      toast({
        title: "Website analysis complete",
        description: "We've gathered insights from your website to personalize your experience.",
      });
    } catch (error: any) {
      console.error("Website analysis error:", error);
      setAnalysisError(error.message || "Failed to analyze website. Please check the URL and try again.");
      toast({
        title: "Analysis failed",
        description: error.message || "We couldn't analyze your website. Please check the URL and try again.",
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  return {
    analyzeWebsite,
    isAnalyzing,
    analysisError
  };
};
